import { QualityValue } from "../DataProgram/Measurement"

export interface MetricRequest {
    /**
     * workflow ID
     */
    workflow: string
    cost: number
    latency: number
    quality: {
        [attrId: string]: QualityValue | undefined
    }
}

export function compareByQuality(attrId: string) {
    return (one: MetricRequest, another: MetricRequest): number => {
        const oneValue = one.quality[attrId]
        const anotherValue = another.quality[attrId]

        return oneValue === undefined || anotherValue === undefined
            ? 0
            : oneValue.value - anotherValue.value
    }
}

export function compareByCost(
    one: MetricRequest,
    another: MetricRequest
): number {
    return one.cost - another.cost
}

export function compareByCostSimilarityTo(request: MetricRequest) {
    return (one: MetricRequest, another: MetricRequest) => {
        return (
            Math.abs(another.cost - request.cost) -
            Math.abs(one.cost - request.cost)
        )
    }
}

export function isSame(one: MetricRequest, another: MetricRequest): boolean {
    return (
        one.cost === another.cost &&
        one.latency === another.latency &&
        Object.entries(one.quality).every(([name, quality]) => {
            const anotherQuality = another.quality[name]

            return (
                quality !== undefined &&
                anotherQuality !== undefined &&
                quality.value === anotherQuality.value
            )
        })
    )
}

export function dedupeAndSortVariants(
    chosenQualityName: string,
    variants: MetricRequest[]
) {
    // We don't care latency for now, because we don't show them on UI
    const qualityDedupedVariants = [...variants]
        .sort(compareByCost)
        .filter((variant, index, items) => {
            // take the cheapest variant
            return (
                items.findIndex(
                    v => compareByQuality(chosenQualityName)(variant, v) <= 0
                ) === index
            )
        })
    const costDedupedVariants = qualityDedupedVariants
        .sort(compareByQuality(chosenQualityName))
        .reverse()
        .filter(
            (variant, index, items) =>
                // take variant with highest quality
                items.findIndex(v => compareByCost(variant, v) >= 0) === index
        )

    return costDedupedVariants.reverse()
}

export function getMostSimilarTo(
    variant: MetricRequest,
    variants: MetricRequest[]
): MetricRequest | undefined {
    return [...variants].sort(compareByCostSimilarityTo(variant)).pop()
}

export function getQualityDisplayName(
    variant: MetricRequest,
    qualityName: string
): string {
    const quality = variant.quality[qualityName]

    return quality?.["ui:name"] === undefined ? qualityName : quality["ui:name"]
}

export function getAllQualityNames(variant: MetricRequest): string[] {
    return Object.keys(variant.quality)
}

export function getOneQualityName(variant: MetricRequest): string | undefined {
    return getAllQualityNames(variant)[0]
}

export function chooseQualityName(
    variant: MetricRequest,
    qualityName: string
): MetricRequest {
    return {
        ...variant,
        quality: { [qualityName]: variant.quality[qualityName] }
    }
}
